// models
import {
  EAccessOptions,
  type IConnectionAccess,
} from "@/components/environment/connection-access/connection-access-modal/connection-access-modal.model";
import {
  EHuggingFaceEnvVariableNames,
  EInferenceType,
  ENimEnvVariableNames,
  LLM_IMAGE_FOR_HUGGING_FACE,
  MAX_REPLICAS_DEFAULT_VALUE,
  MIN_REPLICAS_DEFAULT_VALUE,
  NIM_IMAGE_PREFIX,
  SERVING_PORT_SERVER_PORT,
} from "@/models/inference.model";
import {
  EWorkloadType,
  type IUISuperWorkloadCreationRequest,
  type IUIInferenceCreation,
  type IUIWorkloadCreation,
  type IUIWorkloadSpecificEnv,
} from "@/models/workload.model";
import type { IUIWorkloadEnvSectionModel } from "@/components/section/environment-section/environment-section.model";
import type { IComputeSectionData } from "@/components/section/compute-resource-section/compute-resource-section.models";
import type { Resources } from "@/swagger-models/org-unit-service-client";
import {
  type InferenceSpecificRunParams,
  ServingPortAccessAuthorizationTypeEnum,
  type WorkloadCreationRequest,
  type EnvironmentAsset,
  type EnvironmentVariableOfAsset,
  type InferenceCreationRequestV2,
  type SpecificRunServingPortAccessServingPortAccess,
  type ComputeFields,
  type PvcInstance,
  type HostPathInstance,
  type NfsInstance,
  type CommonStorageFields,
  type AutoScaling,
  AssetKind,
} from "@/swagger-models/assets-service-client";
import { ENimAccessModelMethod } from "@/components/section/nim-model-section/nim-access-model-selector/nim-access-model-selector.model";
import {
  Source,
  Phase,
  type Workload,
  type Inference,
  type InferenceCreationRequest,
  type InferenceSpecSpec,
  type EnvironmentVariableSecret,
  type EnvironmentVariable,
  type ServingPort,
} from "@/swagger-models/workloads-client";
import type { Inference as InferenceFlat, InferenceUpdateSpecSpec } from "@/swagger-models/workloads-client";

import type { IItemizedListItem } from "@/components/common/runai-itemized-list/runai-itemized-list.model";
import type { IAutoScaleData } from "@/components/section/compute-resource-section/auto-scale-section";
import { type TWorkloadsToConvert, workloadUtil } from "@/utils/workload.util/workload.util";
import { DATA_SOURCE_TYPE, type TDataSourceKinds, type IUIVolume } from "@/models/data-source.model";
import type { ClusterDependencyStatus, DisplayedCluster } from "@/swagger-models/cluster-service-client";

// utils
import { fallbackDefaultIfNullOrUndefined, isEqual, omit, pick } from "@/utils/common.util/common.util";
import { workloadCreateFormUtil } from "@/utils/workload-create-form.util/workload-create-form.util";
import { EServerType } from "@/components/section/hugging-face-environment-section/server-type";
import { is } from "quasar";
import { dataSourceUtil } from "../data-source.util/data-source.util";
import { workloadHybridUtil } from "../workload-hybrid.util/workload-hybrid.util";

export const inferenceWorkloadUtil = {
  getEmptyUIInferenceCreation,
  getEmptyUINimInferenceCreation,
  getEmptyUIHfInferenceCreation,
  getInferenceComputeResourceData,
  getEnvironmentRunParamsData,
  getHuggingFaceTokenValue,
  getEnvironmentVariableValue,
  excludeEnvironmentVariablesByName,
  getInferenceCreationRequestV2,
  convertInferenceToWorkloadUI,
  getInferenceWorkloadCreationRequest,
  getInferenceRequestV2SpecificRunParams,
  getNimInferenceWorkloadCreationRequest,
  getHFInferenceWorkloadCreationRequest,
  isLatencyEnableOnCluster,
  isAutoScaleEnableOnCluster,
  convertInferenceToWorkload,
  servingPortInternalToolInfo,
  servingPortAccess,
  getUIAutoScaleEmptyState,
  getUIServingPortEmptyState,
  convertAutoScaleUIToFlat,
  convertAutoScaleFlatToUI,
  isInferenceSupportedDataSource,
  isInferenceSupportedDataSourceInCluster219,
  getInferenceSupportedDataSources,
  getDisabledInfoForDataSource,
  getHybridWorkloadUIModel,
  prepareUpdateInferenceFlatRequest,

  // private functions
  _getAuthorizationType,
  _getServingPortAccessOption,
  _getFlatWorkloadData,
  _getFlatInferenceWorkloadData,
  _addNimEnvironmentVariables,
  _fillDatasourceData,
};

export interface IWorkloadCreateOptions {
  storage?: {
    dataSource: PvcInstance | HostPathInstance | NfsInstance;
    kind: keyof CommonStorageFields;
  };
  genericSecretName?: string;
}

function convertInferenceToWorkload(inferenceWorkload: Inference, tenantId: number): Workload {
  return {
    source: Source.ControlPlane,
    conditions: [{ status: "", type: "", lastTransitionTime: null }],
    priorityClassName: "",
    type: EWorkloadType.Inference,
    name: inferenceWorkload.name,
    id: inferenceWorkload.workloadId,
    clusterId: inferenceWorkload.clusterId,
    projectName: "",
    projectId: inferenceWorkload.projectId,
    departmentName: "",
    departmentId: inferenceWorkload.departmentId || "",
    namespace: "",
    createdAt: inferenceWorkload.createdAt,
    phase: inferenceWorkload.actualPhase || Phase.Creating,
    k8sPhase: "",
    tenantId: tenantId,
    runningPods: 0,
    phaseUpdatedAt: "",
    k8sPhaseUpdatedAt: "",
    updatedAt: "",
    deletedAt: null,
    submittedBy: inferenceWorkload.createdBy,
    priority: null,
  };
}

function getEmptyUINimInferenceCreation(specificEnv?: IUIWorkloadSpecificEnv): IUIInferenceCreation {
  const uiWorkload: IUIWorkloadCreation = getEmptyUIInferenceCreation(specificEnv);
  const modelCreateUi: IUIInferenceCreation = {
    ...uiWorkload,
    image: "",
    inferenceType: EInferenceType.Nim,
    accessFromStorage: false,
    accessModelMethod: ENimAccessModelMethod.Manually,
  };
  return modelCreateUi;
}

function getEmptyUIHfInferenceCreation(specificEnv?: IUIWorkloadSpecificEnv): IUIInferenceCreation {
  const uiInferenceWorkload: IUIInferenceCreation = getEmptyUIInferenceCreation(specificEnv);
  uiInferenceWorkload.serverType = EServerType.VLLM;
  uiInferenceWorkload.image = LLM_IMAGE_FOR_HUGGING_FACE;
  uiInferenceWorkload.inferenceType = EInferenceType.HuggingFace;

  return uiInferenceWorkload;
}

function getEmptyUIInferenceCreation(specificEnv?: IUIWorkloadSpecificEnv): IUIInferenceCreation {
  const uiWorkload: IUIWorkloadCreation = workloadUtil.getEmptyUIWorkloadCreation(specificEnv);
  const uiInferenceWorkload: IUIInferenceCreation = {
    ...uiWorkload,
    specificEnv: {
      ...omit(uiWorkload.specificEnv, ["backoffLimit"]),
      autoScaleData: getUIAutoScaleEmptyState(),
      servingPortAccess: getUIServingPortEmptyState(),
    },
    inferenceType: EInferenceType.AssetsBased,
  };

  return uiInferenceWorkload;
}

function getUIAutoScaleEmptyState(): IAutoScaleData {
  return {
    minReplicas: MIN_REPLICAS_DEFAULT_VALUE,
    maxReplicas: MAX_REPLICAS_DEFAULT_VALUE,
    thresholdMetric: undefined,
    thresholdValue: undefined,
    scaleToZeroRetentionSeconds: undefined,
  };
}

function getUIServingPortEmptyState(): IConnectionAccess {
  return {
    authorizedUsers: null,
    authorizedGroups: null,
    accessOption: EAccessOptions.PUBLIC,
  };
}

function getInferenceComputeResourceData(
  workload: IUIWorkloadCreation,
  projectNodepools: Array<Resources> | undefined | null = [],
): IComputeSectionData {
  const computeData: IComputeSectionData = workloadCreateFormUtil.getComputeResourceData(workload, projectNodepools);
  computeData.autoScaleData = workload.specificEnv.autoScaleData;
  return computeData;
}

function getEnvironmentRunParamsData(workload: IUIWorkloadCreation): IUIWorkloadEnvSectionModel {
  const envSectionData: IUIWorkloadEnvSectionModel = workloadCreateFormUtil.getEnvironmentRunParamsData(workload);
  envSectionData.servingPortAccess = workload.specificEnv.servingPortAccess;
  return envSectionData;
}

function getHuggingFaceTokenValue(environmentVariables: Array<EnvironmentVariableOfAsset> = []): string {
  const tokenEnv: EnvironmentVariableOfAsset | undefined = environmentVariables.find(
    (env: EnvironmentVariableOfAsset) => env.name === EHuggingFaceEnvVariableNames.HF_TOKEN && !env.credential,
  );

  return tokenEnv?.value || "";
}

function getEnvironmentVariableValue(
  keyName: string,
  environmentVariables: Array<EnvironmentVariableOfAsset> = [],
): string {
  const modelEnv: EnvironmentVariableOfAsset | undefined = environmentVariables.find(
    (env: EnvironmentVariableOfAsset) => env.name === keyName,
  );

  return modelEnv?.value || "";
}

function _replaceEnvironmentVariable(
  environmentVariables: Array<EnvironmentVariable> | undefined | null,
  name: string,
  value?: string,
  secret?: EnvironmentVariableSecret,
): Array<EnvironmentVariableOfAsset> {
  let envs = environmentVariables || [];
  envs = envs.filter((env: EnvironmentVariable) => env.name !== name);

  secret ? envs.push({ name, secret }) : envs.push({ name, value });
  return envs;
}

function excludeEnvironmentVariablesByName(
  keyNames: string | Array<string>,
  environmentVariables: Array<EnvironmentVariableOfAsset> = [],
): Array<EnvironmentVariableOfAsset> {
  const keyNamesArray: Array<string> = Array.isArray(keyNames) ? keyNames : [keyNames];
  const keyNamesMap = new Set<string>(keyNamesArray);
  return environmentVariables.filter((env: EnvironmentVariableOfAsset) => env.name && !keyNamesMap.has(env.name));
}

function getNimInferenceWorkloadCreationRequest(
  workload: IUIInferenceCreation,
  compute: ComputeFields,
  workloadCreateOptions?: IWorkloadCreateOptions,
): InferenceCreationRequest {
  const workloadCreationRequest: InferenceCreationRequest = _getFlatInferenceWorkloadData(workload, compute);

  if (!workload.modelName) {
    throw new Error("Model name is missing!");
  }

  // nim specific params
  if (!workloadCreationRequest.spec) {
    workloadCreationRequest.spec = {};
  }

  workloadCreationRequest.spec.image = `${NIM_IMAGE_PREFIX}/${workload.modelName}`;
  workloadCreationRequest.spec.security = {
    ...workloadCreationRequest.spec.security,
    runAsGid: 1000,
    runAsUid: 1000,
  };

  workloadCreationRequest.spec.compute = {
    ...workloadCreationRequest.spec.compute,
    largeShmRequest: true,
  };

  workloadCreationRequest.spec.servingPort = {
    ...workloadCreationRequest.spec.servingPort,
    container: SERVING_PORT_SERVER_PORT,
    protocol: "http",
  };

  if (workloadCreateOptions?.storage) {
    workloadCreationRequest.spec = _fillDatasourceData(
      workloadCreationRequest.spec,
      workloadCreateOptions.storage.dataSource,
      workloadCreateOptions.storage.kind,
      ENimEnvVariableNames.NimCachePath,
    );
  }

  workloadCreationRequest.spec.environmentVariables = _addNimEnvironmentVariables(
    workloadCreationRequest.spec.environmentVariables || [],
  );

  if (workload.profileNameOrHash) {
    workloadCreationRequest.spec.environmentVariables = _replaceEnvironmentVariable(
      workloadCreationRequest.spec.environmentVariables,
      ENimEnvVariableNames.NimModelProfile,
      workload.profileNameOrHash,
    );
  }

  if (workload.token) {
    workloadCreationRequest.spec.environmentVariables = _replaceEnvironmentVariable(
      workloadCreationRequest.spec.environmentVariables,
      ENimEnvVariableNames.NgcApiKey,
      workload.token,
    );
  }

  if (workloadCreateOptions?.genericSecretName) {
    workloadCreationRequest.spec.environmentVariables = _replaceEnvironmentVariable(
      workloadCreationRequest.spec.environmentVariables,
      ENimEnvVariableNames.NgcApiKey,
      undefined,
      {
        name: workloadCreateOptions.genericSecretName,
        key: ENimEnvVariableNames.NgcApiKey,
      },
    );
  }

  return workloadCreationRequest;
}

function getHFInferenceWorkloadCreationRequest(
  workload: IUIInferenceCreation,
  compute: ComputeFields,
  workloadCreateOptions?: IWorkloadCreateOptions,
): InferenceCreationRequest {
  const workloadCreationRequest: InferenceCreationRequest = _getFlatInferenceWorkloadData(workload, compute);

  // hf specific params
  if (!workloadCreationRequest.spec) {
    workloadCreationRequest.spec = {};
  }

  if (!workload.serverType) {
    throw new Error("Missing server type: The workload object must include a valid 'serverType' property.");
  }

  const modelFlag = workload.serverType === EServerType.TGI ? "--model-id" : "--model";
  workloadCreationRequest.spec.args = `${modelFlag} ${workload.modelName} ${
    workloadCreationRequest.spec.args || ""
  }`.trim();
  workloadCreationRequest.spec.image = workload.image;
  workloadCreationRequest.spec.servingPort = {
    ...workloadCreationRequest.spec.servingPort,
    container: SERVING_PORT_SERVER_PORT,
    protocol: "http",
  };

  if (workloadCreateOptions?.storage) {
    workloadCreationRequest.spec = _fillDatasourceData(
      workloadCreationRequest.spec,
      workloadCreateOptions.storage.dataSource,
      workloadCreateOptions.storage.kind,
      EHuggingFaceEnvVariableNames.HF_HOME,
    );
  }

  if (workloadCreationRequest.spec.compute && workloadCreationRequest.spec.compute.gpuDevicesRequest === 0) {
    workloadCreationRequest.spec.compute.gpuDevicesRequest = undefined;
  }

  if (!workloadCreationRequest.spec.environmentVariables) {
    workloadCreationRequest.spec.environmentVariables = [];
  }

  if (workload.token) {
    workloadCreationRequest.spec.environmentVariables = excludeEnvironmentVariablesByName(
      EHuggingFaceEnvVariableNames.HF_TOKEN,
      workloadCreationRequest.spec.environmentVariables,
    );

    workloadCreationRequest.spec.environmentVariables.push({
      name: EHuggingFaceEnvVariableNames.HF_TOKEN,
      value: workload.token || "",
    });
  }

  if (workloadCreateOptions?.genericSecretName) {
    workloadCreationRequest.spec.environmentVariables = excludeEnvironmentVariablesByName(
      EHuggingFaceEnvVariableNames.HF_TOKEN,
      workloadCreationRequest.spec.environmentVariables,
    );
    workloadCreationRequest.spec.environmentVariables.push({
      name: EHuggingFaceEnvVariableNames.HF_TOKEN,
      secret: {
        name: workloadCreateOptions.genericSecretName,
        key: EHuggingFaceEnvVariableNames.HF_TOKEN,
      },
    });
  }

  return workloadCreationRequest;
}

function _fillDatasourceData(
  nimWorkloadSpec: InferenceSpecSpec,
  storage: PvcInstance | HostPathInstance | NfsInstance | undefined,
  kind: keyof CommonStorageFields | undefined,
  envVarName: string,
): InferenceSpecSpec {
  if (kind) {
    nimWorkloadSpec.storage = {
      [kind]: [storage],
    };
  }

  nimWorkloadSpec.environmentVariables = excludeEnvironmentVariablesByName(
    envVarName,
    nimWorkloadSpec.environmentVariables || [],
  );
  const path: string | undefined | null = storage?.path;

  if (path) {
    nimWorkloadSpec.environmentVariables?.push({ name: envVarName, value: path });
  }

  return nimWorkloadSpec;
}

function _addNimEnvironmentVariables(
  environmentVariables: Array<EnvironmentVariableOfAsset>,
): Array<EnvironmentVariableOfAsset> {
  environmentVariables = excludeEnvironmentVariablesByName(
    [
      ENimEnvVariableNames.OutlinesCacheDir,
      ENimEnvVariableNames.NimServerPort,
      ENimEnvVariableNames.NimJsonlLogging,
      ENimEnvVariableNames.NimLogLevel,
    ],
    environmentVariables,
  );

  environmentVariables?.push(
    { name: ENimEnvVariableNames.OutlinesCacheDir, value: "/tmp/outlines" },
    { name: ENimEnvVariableNames.NimServerPort, value: String(SERVING_PORT_SERVER_PORT) },
    { name: ENimEnvVariableNames.NimJsonlLogging, value: "1" },
    { name: ENimEnvVariableNames.NimLogLevel, value: "INFO" },
  );

  return environmentVariables;
}

function _getFlatInferenceWorkloadData(
  workload: IUIInferenceCreation,
  computeFields: ComputeFields,
  environment?: EnvironmentAsset,
): InferenceCreationRequest {
  const specificEnv = workload.specificEnv;
  const workloadCreationRequest: InferenceCreationRequest = _getFlatWorkloadData(workload, computeFields, environment);

  if (workloadCreationRequest.spec && specificEnv.autoScaleData) {
    workloadCreationRequest.spec.autoscaling = convertAutoScaleUIToFlat(specificEnv.autoScaleData);
    workloadCreationRequest.spec.servingPort = servingPortAccess(specificEnv.servingPortAccess);
  }

  return workloadCreationRequest;
}

function convertAutoScaleUIToFlat(autoScaleData: IAutoScaleData): AutoScaling {
  return {
    minReplicas: autoScaleData.minReplicas,
    maxReplicas: autoScaleData.maxReplicas,
    scaleToZeroRetentionSeconds: autoScaleData.scaleToZeroRetentionSeconds || undefined,
    metric: autoScaleData.thresholdMetric || undefined,
    metricThreshold: autoScaleData.thresholdValue || undefined,
  };
}

function convertAutoScaleFlatToUI(autoScaleData: AutoScaling): IAutoScaleData {
  return {
    minReplicas: is.number(autoScaleData.minReplicas) ? autoScaleData.minReplicas : MIN_REPLICAS_DEFAULT_VALUE,
    maxReplicas: is.number(autoScaleData.maxReplicas) ? autoScaleData.maxReplicas : MAX_REPLICAS_DEFAULT_VALUE,
    scaleToZeroRetentionSeconds: autoScaleData.scaleToZeroRetentionSeconds || undefined,
    thresholdMetric: autoScaleData.metric || undefined,
    thresholdValue: autoScaleData.metricThreshold || undefined,
  };
}

function _getFlatWorkloadData(
  workload: IUIInferenceCreation,
  computeFields: ComputeFields,
  environment?: EnvironmentAsset,
): InferenceCreationRequest {
  const specificEnv = workload.specificEnv || {};
  const workloadCreationRequest: InferenceCreationRequest = {
    name: workload.name,
    projectId: workload.projectId.toString(),
    clusterId: workload.clusterId,
    spec: {
      command: specificEnv.command || undefined,
      args: specificEnv.args || undefined,
      nodeType: specificEnv.nodeType || undefined,
      nodePools: specificEnv.nodePools,
      podAffinity: specificEnv.podAffinity || undefined,
      environmentVariables: specificEnv.environmentVariables,
      annotations: specificEnv.annotations?.map((env: IItemizedListItem) => pick(env, "name", "value", "exclude")),
      labels: specificEnv.labels?.map((env: IItemizedListItem) => pick(env, "name", "value", "exclude")),
      tolerations: specificEnv.tolerations,
      compute: computeFields,
    },
  };

  if (environment) {
    workloadCreationRequest.spec = {
      ...workloadCreationRequest.spec,
      image: environment.spec.image || undefined,
      imagePullPolicy: environment.spec.imagePullPolicy || undefined,
      workingDir: environment.spec.workingDir || undefined,
      createHomeDir: environment.spec.createHomeDir || undefined,
    };
  }
  return workloadCreationRequest;
}

/// ---->
function getInferenceCreationRequestV2(
  inference: IUIWorkloadCreation,
  supportingServingPortAccess = false,
  workloadVolumes?: Array<string>,
): InferenceCreationRequestV2 {
  if (!inference.assets.environment) throw "Environment details are missing";
  const workloadCreationRequest: InferenceCreationRequestV2 = {
    meta: {
      name: inference.name,
      projectId: inference.projectId.toString(),
      clusterId: inference.clusterId,
    },
    assets: workloadUtil.getWorkloadAssetsFromUIWorkloadAssets(inference.assets, workloadVolumes),
    specificRunParams: getInferenceRequestV2SpecificRunParams(inference.specificEnv, supportingServingPortAccess),
  };
  return workloadCreationRequest;
}

function convertInferenceToWorkloadUI(
  workload: TWorkloadsToConvert,
  inferenceType: EInferenceType,
  supportingServingPortAccess = false,
  uiVolumes?: Array<IUIVolume>,
  modelName?: string,
  modelAssetBased?: boolean,
): IUIInferenceCreation {
  const baseWorkload: IUIWorkloadCreation = workloadUtil.convertWorkloadToWorkloadUI(workload, uiVolumes);
  const inferenceUI: IUIInferenceCreation = {
    ...baseWorkload,
    inferenceType,
  };

  if (modelName) {
    inferenceUI.modelName = modelName;
  }

  if (modelAssetBased) {
    inferenceUI.modelAssetBased = modelAssetBased;
  }

  const autoScaling = workload.specificRunParams?.autoScaling;
  inferenceUI.specificEnv.autoScaleData = {
    minReplicas: fallbackDefaultIfNullOrUndefined(autoScaling?.minReplicas, 1),
    maxReplicas: fallbackDefaultIfNullOrUndefined(autoScaling?.maxReplicas, 1),
    thresholdMetric: autoScaling?.thresholdMetric || undefined,
    thresholdValue: autoScaling?.thresholdValue || undefined,
    scaleToZeroRetentionSeconds: autoScaling?.scaleToZeroRetentionSeconds || undefined,
  };

  if (supportingServingPortAccess) {
    inferenceUI.specificEnv.servingPortAccess = servingPortInternalToolInfo(
      workload.specificRunParams?.servingPortAccess,
    );
  }

  // delete inferenceUI.specificEnv.allowOverQuota;
  return {
    ...inferenceUI,
    inferenceType: inferenceType || EInferenceType.AssetsBased,
  };
}

function servingPortInternalToolInfo(
  servingPortAccess: SpecificRunServingPortAccessServingPortAccess | ServingPort | undefined | null,
): IConnectionAccess {
  return {
    accessOption: _getServingPortAccessOption(servingPortAccess),
    authorizedGroups: servingPortAccess?.authorizedGroups,
    authorizedUsers: servingPortAccess?.authorizedUsers,
  };
}

function _getServingPortAccessOption(
  servingPortAccess: SpecificRunServingPortAccessServingPortAccess | ServingPort | undefined | null,
): EAccessOptions {
  let authorizationType: EAccessOptions = EAccessOptions.PUBLIC;
  if (!servingPortAccess) return authorizationType;
  switch (servingPortAccess.authorizationType) {
    case ServingPortAccessAuthorizationTypeEnum.Public:
      authorizationType = EAccessOptions.PUBLIC;
      break;
    case ServingPortAccessAuthorizationTypeEnum.AuthenticatedUsers:
      authorizationType = EAccessOptions.EVERYONE;
      break;
    case ServingPortAccessAuthorizationTypeEnum.AuthorizedUsersOrGroups:
      if (servingPortAccess.authorizedGroups) {
        authorizationType = EAccessOptions.GROUPS;
      } else {
        authorizationType = EAccessOptions.SPECIFIC_USERS;
      }
  }

  return authorizationType;
}

function getInferenceRequestV2SpecificRunParams(
  specificEnv: IUIWorkloadSpecificEnv,
  supportingServingPortAccess = false,
): InferenceSpecificRunParams {
  const retSpecificEnv: InferenceSpecificRunParams = workloadUtil.processCommonSpecificEnv(specificEnv, [
    "allowOverQuota",
    "backoffLimit",
    "autoScaleData",
    "servingPortAccess",
  ]);

  retSpecificEnv.autoScaling = specificEnv.autoScaleData;

  if (supportingServingPortAccess) {
    retSpecificEnv.servingPortAccess = servingPortAccess(specificEnv.servingPortAccess);
  }

  return retSpecificEnv;
}

function getInferenceWorkloadCreationRequest(
  workload: IUIInferenceCreation,
  supportingServingPortAccess = false,
  workloadVolumes?: Array<string>,
): WorkloadCreationRequest {
  const workloadCreationRequest: WorkloadCreationRequest = workloadUtil.getWorkloadCreationRequest(
    workload,
    workloadVolumes,
  );

  workloadCreationRequest.specificEnv = {
    ...workloadCreationRequest.specificEnv,
    autoScaling: workload.specificEnv.autoScaleData,
  };

  if (supportingServingPortAccess && workload.specificEnv.servingPortAccess) {
    workloadCreationRequest.specificEnv.servingPortAccess = servingPortAccess(workload.specificEnv.servingPortAccess);
  }

  // Inference does not support backoffLimit
  delete workloadCreationRequest.specificEnv.backoffLimit;

  return workloadCreationRequest;
}

function isLatencyEnableOnCluster(currCluster: DisplayedCluster): boolean {
  if (!currCluster.status?.dependencies?.optional?.knative) return false;
  const dependencyStatus: Record<string, ClusterDependencyStatus> | undefined =
    currCluster.status.dependencies.optional.knative.components;
  return (dependencyStatus && dependencyStatus["hpa"] && dependencyStatus["hpa"].available) || false;
}

function isAutoScaleEnableOnCluster(currCluster: DisplayedCluster): boolean {
  if (!currCluster.status?.dependencies?.optional?.knative) return false;
  const dependencyStatus: Record<string, ClusterDependencyStatus> | undefined =
    currCluster.status.dependencies.optional.knative.components?.knative.components;

  return (
    (dependencyStatus &&
      dependencyStatus["enable-scale-to-zero"] &&
      dependencyStatus["enable-scale-to-zero"].available) ||
    false
  );
}

function servingPortAccess(
  servingPortAccess: IConnectionAccess | null | undefined,
): SpecificRunServingPortAccessServingPortAccess | ServingPort {
  return {
    authorizationType: _getAuthorizationType(servingPortAccess),
    authorizedGroups: servingPortAccess?.authorizedGroups,
    authorizedUsers: servingPortAccess?.authorizedUsers,
  };
}

function _getAuthorizationType(
  servingPortAccess: IConnectionAccess | null | undefined,
): ServingPortAccessAuthorizationTypeEnum | undefined {
  let authorizationType: ServingPortAccessAuthorizationTypeEnum | undefined = undefined;

  if (!servingPortAccess?.accessOption) return authorizationType;

  switch (servingPortAccess.accessOption) {
    case EAccessOptions.PUBLIC:
      // set to undefined since no need to send it to the server
      break;
    case EAccessOptions.EVERYONE:
      authorizationType = ServingPortAccessAuthorizationTypeEnum.AuthenticatedUsers;
      break;
    case EAccessOptions.GROUPS:
    case EAccessOptions.SPECIFIC_USERS:
      authorizationType = ServingPortAccessAuthorizationTypeEnum.AuthorizedUsersOrGroups;
  }

  return authorizationType;
}

/**
 * Determines whether the given asset kind is a supported data source for inference workloads.
 *
 * Supported asset kinds:
 * - PVC (Persistent Volume Claim)
 * - Git repository
 * - ConfigMap
 *
 * @param {AssetKind} kind - The asset kind to check.
 * @returns {boolean} `true` if the asset kind is supported for inference workloads, otherwise `false`.
 */
function isInferenceSupportedDataSource(kind: AssetKind): boolean {
  const supportedAssetKinds = new Set<AssetKind>([AssetKind.Pvc, AssetKind.Git, AssetKind.ConfigMap]);
  return supportedAssetKinds.has(kind);
}

/**
 * Determines whether the given asset kind is a supported data source for inference workloads
 * starting from cluster version 2.19.
 *
 * Supported asset kinds:
 * - HostPath
 * - NFS (Network File System)
 *
 * @param {AssetKind} kind - The asset kind to check.
 * @returns {boolean} `true` if the asset kind is supported for inference workloads in cluster version 2.19+, otherwise `false`.
 */
function isInferenceSupportedDataSourceInCluster219(kind: AssetKind): boolean {
  const supportedAssetKinds = new Set<AssetKind>([AssetKind.HostPath, AssetKind.Nfs, AssetKind.DataVolume]);
  return supportedAssetKinds.has(kind);
}

/**
 * Determines whether an item should be disabled based on its asset kind and cluster version.
 *
 * @param {AssetKind} kind - The asset kind to check.
 * @param {boolean} isClusterSupportedNfsAndHostPath - Whether the cluster supports NFS and HostPath.
 * @returns {string | null} A message indicating why the item should be disabled, or `null` if it's supported.
 */
function getDisabledInfoForDataSource(kind: AssetKind, isClusterSupportedNfsAndHostPath: boolean): string | null {
  const type = dataSourceUtil.isTDataSourceKinds(kind) ? DATA_SOURCE_TYPE[kind] : "";
  if (isInferenceSupportedDataSource(kind)) {
    return null;
  }

  if (isInferenceSupportedDataSourceInCluster219(kind)) {
    return isClusterSupportedNfsAndHostPath
      ? null
      : `Inference workloads don't support data sources of type ${type} for the current version of the cluster.`;
  }

  return `Inference workloads don't support data sources of type ${type}`;
}

/**
 * Retrieves the list of data source types supported for inference workloads.
 *
 * By default, the function includes the following data sources:
 * - PVC (Persistent Volume Claim)
 * - Git repository
 * - ConfigMap
 *
 * If `isVersion219OrHigher` is `true`, additional data sources supported from cluster version 2.19 are included:
 * - HostPath
 * - NFS (Network File System)
 *
 * @param {Object} options - Configuration options.
 * @param {boolean} options.isVersion219OrHigher - Whether the cluster is version 2.19 or later.
 * @returns {TDataSourceKinds[]} A list of supported data source type names.
 */
function getInferenceSupportedDataSources(options: { isVersion219OrHigher: boolean }): TDataSourceKinds[] {
  const baseSupportedSources = [AssetKind.Pvc, AssetKind.Git, AssetKind.ConfigMap];

  if (!options.isVersion219OrHigher) return baseSupportedSources;
  // from cluster 2.19
  const supportedInCluster219: Array<TDataSourceKinds> = [AssetKind.HostPath, AssetKind.Nfs];
  return [...baseSupportedSources, ...supportedInCluster219];
}

function getHybridWorkloadUIModel(inference: InferenceFlat): IUISuperWorkloadCreationRequest {
  const uiModel: IUISuperWorkloadCreationRequest = {
    ...inference,
    spec: {
      ...inference.spec,
      connections: workloadHybridUtil.getConnectionsField(
        inference.spec?.ports || [],
        inference.spec?.exposedUrls || [],
        inference.spec?.relatedUrls || [],
      ),
      compute: workloadHybridUtil.getComputeField(inference.spec?.compute),
      storage: workloadHybridUtil.getStorageField(inference.spec?.storage),
      security: workloadHybridUtil.getSecurityField(inference.spec?.security),
    },
  };

  // Bug fix: When the user sets `minReplicas` to 0 in the API but does not specify
  // `scaleToZeroRetentionSeconds`, Knative defaults this value to 1 minute.
  // To ensure consistency, we explicitly set it to 60 seconds in the UI.
  if (uiModel.spec.autoscaling?.minReplicas === 0 && uiModel.spec.autoscaling.scaleToZeroRetentionSeconds == undefined) {
    uiModel.spec.autoscaling.scaleToZeroRetentionSeconds = 60;
  }

  return uiModel;
}

function prepareUpdateInferenceFlatRequest(
  inference: InferenceFlat,
  modelUi: IUISuperWorkloadCreationRequest,
): InferenceUpdateSpecSpec {
  const originSpec = inference.spec;
  const newSpec = modelUi.spec;

  const compute: ComputeFields | undefined = workloadHybridUtil._getCleanedComputeFieldsFromUI(newSpec.compute || {});
  return {
    command: originSpec?.command !== newSpec.command ? newSpec.command : undefined,
    args: originSpec?.args !== newSpec.args ? newSpec.args : undefined,
    image: originSpec?.image !== newSpec.image ? newSpec.image : undefined,
    imagePullPolicy: originSpec?.imagePullPolicy !== newSpec.imagePullPolicy ? newSpec.imagePullPolicy : undefined,
    workingDir: originSpec?.workingDir !== newSpec.workingDir ? newSpec.workingDir : undefined,
    createHomeDir: originSpec?.createHomeDir !== newSpec.createHomeDir ? newSpec.createHomeDir : undefined,
    probes: isEqual(originSpec?.probes, newSpec.probes) ? undefined : newSpec.probes,
    environmentVariables: isEqual(originSpec?.environmentVariables, newSpec.environmentVariables)
      ? undefined
      : newSpec.environmentVariables,
    autoscaling: isEqual(originSpec?.autoscaling, newSpec.autoscaling) ? undefined : newSpec.autoscaling,
    compute: isEqual(compute, originSpec?.compute) ? undefined : compute,
    nodePools: isEqual(originSpec?.nodePools, newSpec.nodePools) ? undefined : modelUi.spec.nodePools,
    nodeType: originSpec?.nodeType !== newSpec.nodeType ? undefined : modelUi.spec.nodeType,
  };
}
